import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useWorkspace } from "../app/workspace";
import { usd, num, pct, relDate, shortDate, timeAgo } from "../lib/format";
import type { Account, Contact, HealthBand, PlaybookRun, Playbook } from "../lib/types";
import { Icon } from "../components/icons";
import { HealthRing, Meter, Avatar, Monogram, HealthBadge, SignalDot, Empty } from "../components/ui";
import { Compose, type Prefill } from "../components/Compose";
import * as work from "../lib/workClient";
import { buildGraphFromWorkspace } from "../lib/graphFromWorkspace";
import { contractsForOrg, instancesForOrg, childOrgs, orgById, resolvePerson, bandOf } from "../lib/graph";
import { LayoutGrid } from "../components/widgets";
import { LayoutEditor } from "../components/LayoutEditor";
import { PlanRuns } from "../components/PlanRuns";
import type { Layout } from "../lib/types";

const sentimentBand: Record<Contact["sentiment"], HealthBand> = {
  positive: "good",
  neutral: "watch",
  negative: "risk",
};

type Tab = "overview" | "relationships" | "plan" | "signals" | "timeline" | "contacts";
const tabs: { key: Tab; label: string }[] = [
  { key: "overview", label: "Overview" },
  { key: "relationships", label: "Relationships" },
  { key: "plan", label: "Plan" },
  { key: "signals", label: "Signals" },
  { key: "timeline", label: "Activity" },
  { key: "contacts", label: "Contacts" },
];

/** Turn a raw attribute key into a human label: "go_live_date" → "Go live date". */
function humanKey(key: string): string {
  const spaced = key.replace(/_/g, " ").trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function formatAttr(value: string | number | boolean): string {
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return num(value);
  if (value === "") return "—";
  return value;
}

function ShareModal({ accountId, accountName, onClose }: { accountId: string; accountName: string; onClose: () => void }) {
  const { ws } = useWorkspace();
  const [url, setUrl] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const create = async () => {
    setCreating(true);
    const r = await work.createShare(ws, accountId);
    setUrl(r.url); setCreating(false);
  };

  return (
    <div className="cmdk-overlay" onClick={onClose}>
      <div className="compose-modal" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
        <div className="compose-modal__hd"><div className="row" style={{ gap: 8 }}><Icon name="external" size={16} /><b>Share {accountName} with the customer</b></div><button className="icon-btn" onClick={onClose}><Icon name="dot" size={16} /></button></div>
        <div className="compose-modal__bd">
          {url ? (
            <>
              <p className="faint" style={{ fontSize: 13, marginBottom: 10 }}>Anyone with this link can view a read-only, customer-safe status page — no login required.</p>
              <div className="secret-box">{url}</div>
            </>
          ) : (
            <p className="faint" style={{ fontSize: 13 }}>Generate a secure, read-only link to {accountName}'s health, success plan, renewal, and recent wins. Internal notes, risk agents, and CSM tasks are never shown.</p>
          )}
        </div>
        <div className="compose-modal__ft">
          <span className="faint" style={{ fontSize: 12 }}>Revocable anytime</span>
          {url
            ? <button className="btn btn--primary btn--sm" onClick={() => { navigator.clipboard?.writeText(url); onClose(); }}>Copy link &amp; done</button>
            : <button className="btn btn--primary btn--sm" disabled={creating} onClick={create}><Icon name="external" size={14} /> {creating ? "Creating…" : "Create link"}</button>}
        </div>
      </div>
    </div>
  );
}

function RelationshipsTab({ account }: { account: Account }) {
  const { ws } = useWorkspace();
  const g = useMemo(() => buildGraphFromWorkspace(ws), [ws]);
  const org = orgById(g, account.id);
  if (!org) return <div className="card"><div className="card__bd"><Empty>No relationship graph for this account.</Empty></div></div>;

  const parent = org.parentOrgId ? orgById(g, org.parentOrgId) : undefined;
  const siblings = parent ? childOrgs(g, parent.id).filter((o) => o.id !== org.id) : [];
  const contracts = contractsForOrg(g, org.id);
  const instances = instancesForOrg(g, org.id);
  const instanceIds = new Set(instances.map((i) => i.id));
  const personIds = [...new Set(g.users.filter((u) => instanceIds.has(u.instanceId)).map((u) => u.personId))];
  const people = personIds.map((pid) => resolvePerson(g, pid)).filter((r): r is NonNullable<typeof r> => r !== null);

  return (
    <div style={{ display: "grid", gap: 20 }}>
      <div className="card">
        <div className="card__hd">
          <div><h3>Corporate structure</h3><span className="sub">Where this account sits in the graph</span></div>
          <Link to={`/relationships?account=${account.id}`} className="btn btn--ghost btn--sm">Open full graph <Icon name="arrowRight" size={13} /></Link>
        </div>
        <div className="card__bd">
          {parent ? (
            <>
              <Link to={`/relationships/${parent.id}`} className="affil" style={{ textDecoration: "none" }}>
                <span className="t-ic" style={{ background: parent.color, width: 30, height: 30 }}>{parent.name[0]}</span>
                <div className="grow"><b style={{ fontSize: 13.5 }}>{parent.name}</b><div className="faint" style={{ fontSize: 12 }}>Parent company · open relationship 360</div></div>
                <span className="tag">rolls up {childOrgs(g, parent.id).length} accounts</span>
              </Link>
              {siblings.length > 0 && (
                <p className="faint" style={{ fontSize: 12.5, marginTop: 10 }}>
                  Sibling accounts: {siblings.map((s, idx) => <span key={s.id}><Link to={`/accounts/${s.id}`}>{s.name}</Link>{idx < siblings.length - 1 ? ", " : ""}</span>)}
                </p>
              )}
            </>
          ) : <p className="faint" style={{ fontSize: 13 }}>Standalone organization — not part of a parent-company group.</p>}
        </div>
      </div>

      {contracts.map((c) => (
        <div className="card" key={c.id}>
          <div className="card__hd"><div><h3>Contract</h3><span className="sub">{c.revenueType === "services" ? "Services engagement" : "Subscription"}</span></div><span className="tree-kind">{c.status}</span></div>
          <div className="card__bd">
            <div className="kv">
              <dt>{c.amountLabel}</dt><dd>{usd(c.amount)}</dd>
              <dt>Term</dt><dd>{shortDate(c.startDate)} → {shortDate(c.endDate)}</dd>
              <dt>Deployments</dt><dd>{instances.length}</dd>
            </div>
          </div>
        </div>
      ))}

      <div className="card">
        <div className="card__hd"><div><h3>Deployments</h3><span className="sub">{instances.length} running for this account</span></div></div>
        <div style={{ overflowX: "auto" }}>
          <table className="table">
            <thead><tr><th>Deployment</th>{instances[0]?.metrics.map((m) => <th key={m.label}>{m.label}</th>)}<th>Health</th></tr></thead>
            <tbody>
              {instances.map((i) => (
                <tr key={i.id}>
                  <td><Link to={`/projects/${i.id}`} style={{ fontWeight: 600, fontSize: 13 }}>{i.name}</Link></td>
                  {i.metrics.map((m) => <td key={m.label} className="faint" style={{ fontSize: 12.5 }}>{m.value}</td>)}
                  <td><HealthBadge band={bandOf(i.health)} label={`${i.health}`} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <div className="card__hd"><div><h3>People &amp; logins</h3><span className="sub">Contacts resolved across every login on this account</span></div></div>
        <div className="card__bd" style={{ display: "grid", gap: 8 }}>
          {people.length === 0 ? <Empty>No people resolved yet.</Empty> : people.map((res) => {
            const others = res.appearsUnder.filter((a) => a.org.id !== account.id && a.org.accountId);
            return (
              <div className="affil" key={res.person.id}>
                <Avatar name={res.person.name} size="sm" />
                <div className="grow">
                  <b style={{ fontSize: 13 }}>{res.person.name}</b>
                  <span className="faint" style={{ fontSize: 12 }}> · {res.person.title}</span>
                  {others.length > 0 && <div className="faint" style={{ fontSize: 12 }}>Also appears under {others.map((o) => o.org.name).join(", ")}</div>}
                </div>
                <span className="tag">{res.users.length} login{res.users.length === 1 ? "" : "s"}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const activityIcon: Record<work.ActivityKind, string> = {
  note: "doc", signal: "pulse", task: "check", playbook: "route", survey: "star",
  renewal: "refresh", milestone: "target", meeting: "calendar", event: "bell",
};
const toneColor = (t: work.ActivityTone) => (t === "good" ? "var(--good)" : t === "risk" ? "var(--risk)" : t === "watch" ? "var(--watch)" : "var(--muted)");

function ActivityTab({ account }: { account: Account }) {
  const { ws } = useWorkspace();
  const [mode, setMode] = useState<work.Mode>("local");
  const [entries, setEntries] = useState<work.ActivityEntry[] | null>(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [thinking, setThinking] = useState(false);
  const [aiPending, setAiPending] = useState(false);
  const [live, setLive] = useState<boolean | null>(null);
  const [filter, setFilter] = useState<work.ActivityKind | "all">("all");

  const refresh = () => work.loadActivity(ws, account.id).then((r) => { setMode(r.mode); setEntries(r.entries); });
  useEffect(() => { refresh(); work.aiStatus().then((r) => setLive(r.available)); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [ws, account.id]);

  const post = async () => {
    if (!draft.trim()) return;
    setBusy(true);
    await work.addNote(mode, ws, account.id, draft.trim(), aiPending);
    setDraft(""); setAiPending(false);
    await refresh();
    setBusy(false);
  };
  const generate = async () => {
    setThinking(true);
    const d = await work.draftNote(ws, account.id);
    if (d) { setDraft(d.body); setAiPending(true); }
    setThinking(false);
  };

  const kinds = useMemo(() => {
    const set = new Set((entries ?? []).map((e) => e.kind));
    return Array.from(set);
  }, [entries]);
  const shown = (entries ?? []).filter((e) => filter === "all" || e.kind === filter);

  return (
    <div style={{ display: "grid", gap: 16 }}>
      {/* note composer */}
      <div className="card">
        <div className="card__hd">
          <div><h3>Add a note</h3><span className="sub">Logged to the timeline</span></div>
          {live !== null && <span className={`mode-pill ${live ? "live" : ""}`}><i />{live ? "Live AI" : "Built-in AI"}</span>}
        </div>
        <div className="card__bd">
          <textarea className="input" style={{ width: "100%", minHeight: 84, resize: "vertical", fontFamily: "inherit" }}
            placeholder={`What's happening with ${account.name}?`} value={draft} onChange={(e) => { setDraft(e.target.value); setAiPending(false); }} />
          <div className="row between" style={{ marginTop: 10 }}>
            <button className="btn btn--ghost btn--sm" onClick={generate} disabled={thinking}><Icon name="sparkle" size={14} /> {thinking ? "Drafting…" : "Draft with AI"}</button>
            <div className="row" style={{ gap: 8 }}>
              {aiPending && <span className="tag" style={{ alignSelf: "center" }}>AI draft — review before saving</span>}
              <button className="btn btn--primary btn--sm" onClick={post} disabled={busy || !draft.trim()}>{busy ? "Saving…" : "Save note"}</button>
            </div>
          </div>
        </div>
      </div>

      {/* unified feed */}
      <div className="card">
        <div className="card__hd">
          <div><h3>Activity</h3><span className="sub">Everything that touched this account · newest first</span></div>
          <select className="input" style={{ padding: "6px 10px", fontSize: 13 }} value={filter} onChange={(e) => setFilter(e.target.value as work.ActivityKind | "all")}>
            <option value="all">All activity</option>
            {kinds.map((k) => <option key={k} value={k}>{k[0].toUpperCase() + k.slice(1)}</option>)}
          </select>
        </div>
        <div className="card__bd">
          {entries === null ? <Empty>Loading activity…</Empty> : shown.length === 0 ? <Empty>No activity in this filter.</Empty> : (
            <div style={{ display: "grid", gap: 2 }}>
              {shown.map((e) => (
                <div key={e.id} className="row" style={{ gap: 12, alignItems: "flex-start", padding: "11px 0", borderBottom: "1px solid var(--line-soft)" }}>
                  <div style={{ width: 30, height: 30, borderRadius: 8, background: "var(--surface-2)", display: "grid", placeItems: "center", color: toneColor(e.tone), flexShrink: 0, marginTop: 1 }}>
                    <Icon name={activityIcon[e.kind]} size={15} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="row" style={{ gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      {e.ai && <span className="tag" style={{ color: "var(--corona-ink)" }}>AI</span>}
                      <b style={{ fontSize: 14 }}>{e.title}</b>
                    </div>
                    {e.detail && <p style={{ fontSize: 13, color: "var(--muted)", margin: "3px 0 0", lineHeight: 1.5 }}>{e.detail}</p>}
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div className="faint" style={{ fontSize: 12 }}>{shortDate(e.at)}</div>
                    {e.actor && <div className="faint" style={{ fontSize: 11.5, marginTop: 2 }}>{e.actor}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PlanTab({ account }: { account: Account }) {
  const { ws } = useWorkspace();
  const [mode, setMode] = useState<work.Mode>("local");
  const [runs, setRuns] = useState<PlaybookRun[]>([]);
  const [playbooks, setPlaybooks] = useState<Playbook[]>([]);
  const [applying, setApplying] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);

  const graph = useMemo(() => buildGraphFromWorkspace(ws), [ws]);
  const org = orgById(graph, account.id);
  const relationship = org?.parentOrgId ? orgById(graph, org.parentOrgId) : undefined;

  const refresh = async () => {
    const r = await work.loadPlaybookRuns(ws); setMode(r.mode);
    setRuns(r.runs.filter((run) => run.accountId === account.id && run.scopeKind !== "relationship"));
    setPlaybooks((await work.loadPlaybooks(ws)).playbooks);
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [ws, account.id]);
  const say = (m: string) => { setFlash(m); setTimeout(() => setFlash(null), 2800); };

  return (
    <div style={{ display: "grid", gap: 16 }}>
      {flash && <div className="badge badge--good">{flash}</div>}
      <div className="row between">
        <div className="faint" style={{ fontSize: 13 }}>
          {runs.length} account plan{runs.length === 1 ? "" : "s"}
          {relationship && <> · <Link to={`/relationships/${relationship.id}`}>relationship plans for {relationship.name}</Link></>}
        </div>
        <button className="btn btn--primary btn--sm" onClick={() => setApplying(true)}><Icon name="route" size={14} /> Apply a playbook</button>
      </div>

      <PlanRuns runs={runs} mode={mode} onChanged={refresh} />

      {applying && <ApplyPlaybook account={account} relationship={relationship ? { id: relationship.id, name: relationship.name } : undefined}
        playbooks={playbooks} onClose={() => setApplying(false)}
        onApply={async (playbookId, opts) => { const r = await work.launchPlaybook(mode, ws, playbookId, opts); setApplying(false); if (r) { say("Plan applied — tasks scheduled"); refresh(); } }} />}
    </div>
  );
}

function ApplyPlaybook({ account, relationship, playbooks, onClose, onApply }: {
  account: Account; relationship?: { id: string; name: string }; playbooks: Playbook[];
  onClose: () => void; onApply: (playbookId: string, opts: work.LaunchScope) => void;
}) {
  const applicable = playbooks.filter((p) => p.steps.some((s) => s.dependsOn?.length || s.durationDays));  // scheduled plays first
  const list = applicable.length ? applicable : playbooks;
  const [playbookId, setPlaybookId] = useState(list.find((p) => p.id === "p_onboard")?.id ?? list[0]?.id ?? "");
  const [scope, setScope] = useState<"account" | "relationship">("account");
  const [start, setStart] = useState("2026-09-01");
  const pb = playbooks.find((p) => p.id === playbookId);
  const canRel = !!relationship && (pb?.scope === "relationship" || pb?.scope === "both");

  return (
    <div className="cmdk-overlay" onClick={onClose}>
      <div className="compose-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <div className="compose-modal__hd"><div className="row" style={{ gap: 8 }}><Icon name="route" size={16} /><b>Apply a playbook</b></div><button className="icon-btn" onClick={onClose}><Icon name="dot" size={16} /></button></div>
        <div className="compose-modal__bd" style={{ display: "grid", gap: 14 }}>
          <label style={{ display: "grid", gap: 5 }}>
            <span className="labeled__l">Playbook</span>
            <select className="inp" value={playbookId} onChange={(e) => setPlaybookId(e.target.value)}>
              {list.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.steps.length} steps)</option>)}
            </select>
          </label>
          <div>
            <span className="labeled__l">Apply at</span>
            <div className="seg-control" style={{ marginTop: 6 }}>
              <button className={scope === "account" ? "on" : ""} onClick={() => setScope("account")}>Account · {account.name}</button>
              <button className={scope === "relationship" ? "on" : ""} disabled={!canRel} onClick={() => canRel && setScope("relationship")} title={canRel ? "" : relationship ? "This playbook isn't relationship-scoped" : "No parent relationship for this account"}>
                Relationship{relationship ? ` · ${relationship.name}` : ""}
              </button>
            </div>
          </div>
          <label style={{ display: "grid", gap: 5 }}>
            <span className="labeled__l">Start date</span>
            <input className="inp" type="date" value={start} onChange={(e) => setStart(e.target.value)} />
          </label>
          {pb && <div className="faint" style={{ fontSize: 12 }}>{pb.steps.filter((s) => s.kind !== "trigger" && s.kind !== "wait" && s.kind !== "branch").length} tasks will be created, scheduled by their dependencies.</div>}
        </div>
        <div className="compose-modal__hd" style={{ borderTop: "1px solid var(--line-soft)", borderBottom: "none", justifyContent: "flex-end", gap: 8 }}>
          <button className="btn btn--sm" onClick={onClose}>Cancel</button>
          <button className="btn btn--primary btn--sm" disabled={!playbookId} onClick={() => onApply(playbookId, scope === "relationship" && relationship
            ? { accountId: account.id, scopeKind: "relationship", scopeId: relationship.id, scopeName: relationship.name, planStartDate: start }
            : { accountId: account.id, scopeKind: "account", scopeId: account.id, scopeName: account.name, planStartDate: start })}>Apply plan</button>
        </div>
      </div>
    </div>
  );
}

export default function AccountDetail() {
  const { id } = useParams<{ id: string }>();
  const { ws, accountById, userById } = useWorkspace();
  const account = id ? accountById(id) : undefined;
  const [tab, setTab] = useState<Tab>("overview");
  const [compose, setCompose] = useState<Prefill | null>(null);
  const [sharing, setSharing] = useState(false);
  const [mode, setMode] = useState<work.Mode>("local");
  const [layout, setLayout] = useState<Layout | null>(null);
  const [editing, setEditing] = useState(false);
  useEffect(() => { work.loadLayouts(ws).then((r) => { setMode(r.mode); setLayout(r.layouts.find((l) => l.entity === "account") ?? null); }); }, [ws]);
  const saveLayout = async (sections: Layout["sections"]) => {
    if (!layout) return;
    setLayout({ ...layout, sections }); setEditing(false);
    await work.saveLayout(mode, ws, layout.id, sections);
  };

  const signals = useMemo(
    () => (account ? ws.signals.filter((s) => s.accountId === account.id) : []),
    [ws.signals, account]
  );

  if (!account) {
    return (
      <div className="page">
        <Empty>
          <p style={{ marginBottom: 12 }}>That account could not be found.</p>
          <Link to="/accounts" className="btn btn--ghost btn--sm">
            <Icon name="arrowRight" size={14} /> Back to accounts
          </Link>
        </Empty>
      </div>
    );
  }

  const owner = userById(account.ownerId);

  return (
    <div className="page">
      {compose && <Compose prefill={compose} onClose={() => setCompose(null)} />}
      {sharing && <ShareModal accountId={account.id} accountName={account.name} onClose={() => setSharing(false)} />}
      {editing && layout && <LayoutEditor layout={layout} entity="account" onSave={saveLayout} onClose={() => setEditing(false)} />}
      <p className="eyebrow" style={{ marginBottom: 14 }}>
        <Link to="/accounts" className="faint">Accounts</Link> · {account.region}
      </p>

      <div className="detail-hd">
        <Monogram name={account.name} size="lg" />
        <div className="meta">
          <h1>{account.name}</h1>
          <div className="subline">
            <span>{account.domain}</span>
            <span>{account.industry}</span>
            <span>{account.region}</span>
            {account.attributes.segment !== undefined && <span>{String(account.attributes.segment)}</span>}
            {owner && (
              <span className="row" style={{ gap: 6 }}>
                <Avatar name={owner.name} color={owner.color} size="sm" /> {owner.name}
              </span>
            )}
            <span className="num" style={{ fontWeight: 600, color: "var(--ink)" }}>{usd(account.arr)} ARR</span>
          </div>
        </div>
        <div className="row" style={{ gap: 20, alignItems: "center" }}>
          <HealthRing value={account.health} size={72} stroke={6} />
          <div style={{ display: "grid", gap: 8 }}>
            <button className="btn btn--primary">
              <Icon name="route" size={15} /> Start playbook
            </button>
            <button className="btn btn--ghost" onClick={() => setSharing(true)}>
              <Icon name="external" size={15} /> Share with customer
            </button>
          </div>
        </div>
      </div>

      <div className="detail-grid">
        {/* main column */}
        <div>
          <div className="tabs">
            {tabs.map((t) => (
              <button
                key={t.key}
                className={`tab ${tab === t.key ? "active" : ""}`}
                onClick={() => setTab(t.key)}
              >
                {t.label}
                {t.key === "signals" && signals.length > 0 ? ` · ${signals.length}` : ""}
              </button>
            ))}
          </div>

          {tab === "overview" && (
            <div style={{ display: "grid", gap: 16 }}>
              <div className="row between" style={{ alignItems: "center" }}>
                <span className="faint" style={{ fontSize: 12.5 }}>Customer 360 — your saved layout</span>
                <button className="btn btn--ghost btn--sm" onClick={() => setEditing(true)}><Icon name="grid" size={14} /> Customize</button>
              </div>
              {layout ? <LayoutGrid sections={layout.sections} ctx={{ entity: "account", ws, account }} /> : <Empty>Loading layout…</Empty>}
            </div>
          )}

          {tab === "relationships" && <RelationshipsTab account={account} />}

          {tab === "plan" && <PlanTab account={account} />}

          {tab === "signals" && (
            <div className="card">
              <div className="card__hd">
                <div><h3>Signals</h3><span className="sub">Changes detected on this account</span></div>
              </div>
              <div className="card__bd" style={{ paddingTop: 4, paddingBottom: 4 }}>
                {signals.length === 0 ? (
                  <Empty>No signals on this account right now.</Empty>
                ) : (
                  signals.map((s) => (
                    <div className="feed-item" key={s.id} style={{ alignItems: "start" }}>
                      <SignalDot band={s.band} />
                      <div>
                        <b>{s.title}</b>
                        <small>{s.detail}</small>
                        <small className="faint" style={{ marginTop: 2 }}>
                          {s.source} · {s.status}
                        </small>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div className="num" style={{ fontWeight: 600, color: s.impact < 0 ? "var(--risk)" : "var(--good)", fontSize: 13 }}>
                          {s.impact > 0 ? "+" : ""}{s.impact}
                        </div>
                        <time>{timeAgo(s.at)} ago</time>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {tab === "timeline" && <ActivityTab account={account} />}

          {tab === "contacts" && (
            <div className="card">
              <div className="card__hd">
                <div><h3>Contacts</h3><span className="sub">Relationships on the account</span></div>
              </div>
              <div style={{ overflowX: "auto" }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Role</th>
                      <th>Sentiment</th>
                      <th>Email</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {account.contacts.map((c) => (
                      <tr key={c.email} style={{ cursor: "default" }}>
                        <td>
                          <div className="acct-cell">
                            <Avatar name={c.name} size="sm" />
                            <div>
                              <b>{c.name}</b>
                              <small>{c.title}</small>
                            </div>
                          </div>
                        </td>
                        <td><span className="chip">{c.role}</span></td>
                        <td><HealthBadge band={sentimentBand[c.sentiment]} label={c.sentiment} /></td>
                        <td><span className="faint" style={{ fontSize: 13 }}>{c.email}</span></td>
                        <td style={{ textAlign: "right" }}>
                          <button className="btn btn--sm" onClick={() => setCompose({ to: c.email, toName: c.name, accountId: account.id, subject: `Following up — ${account.name}`, body: `Hi ${c.name.split(" ")[0]},\n\n` })}>
                            <Icon name="mail" size={14} /> Email
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {account.contacts.length === 0 && <Empty>No contacts on file.</Empty>}
            </div>
          )}
        </div>

        {/* side column */}
        <div style={{ display: "grid", gap: 20 }}>
          <div className="card">
            <div className="card__hd"><h3>Renewal</h3><Icon name="calendar" size={16} /></div>
            <div className="card__bd">
              <div className="row between" style={{ alignItems: "baseline", marginBottom: 12 }}>
                <b className="serif" style={{ fontSize: 20 }}>{relDate(account.renewalDate)}</b>
                <span className="faint num" style={{ fontSize: 12.5 }}>{shortDate(account.renewalDate)}</span>
              </div>
              <dl className="kv">
                <dt>ARR</dt>
                <dd>{usd(account.arr)}</dd>
                <dt>Lifecycle stage</dt>
                <dd>{account.lifecycleStage}</dd>
              </dl>
              <div style={{ marginTop: 14 }}>
                <div className="row between" style={{ marginBottom: 7 }}>
                  <span className="muted" style={{ fontSize: 13 }}>Expansion potential</span>
                  <span className="num" style={{ fontWeight: 600 }}>{pct(account.expansionPotential)}</span>
                </div>
                <Meter value={account.expansionPotential} color="var(--up)" />
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card__hd"><h3>Custom fields</h3><Icon name="database" size={16} /></div>
            <div className="card__bd">
              <dl className="kv">
                {Object.entries(account.attributes).map(([k, v]) => (
                  <div key={k} style={{ display: "contents" }}>
                    <dt>{humanKey(k)}</dt>
                    <dd>{formatAttr(v)}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>

          <div className="card">
            <div className="card__hd"><h3>Owner</h3><Icon name="user" size={16} /></div>
            <div className="card__bd">
              {owner ? (
                <div className="row" style={{ gap: 12 }}>
                  <Avatar name={owner.name} color={owner.color} size="lg" />
                  <div>
                    <b style={{ fontSize: 14.5 }}>{owner.name}</b>
                    <div className="muted" style={{ fontSize: 12.5 }}>{owner.role}</div>
                    <div className="faint num" style={{ fontSize: 12, marginTop: 2 }}>
                      {usd(owner.booksArr)} book · {owner.accounts} accounts
                    </div>
                  </div>
                </div>
              ) : (
                <span className="faint">Unassigned</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
